import { api } from '@shared/api';
import type { LearnerLevel, StoryBonusGrant } from './storiesApi';

/**
 * Миссия дня v2 и бой с Граммозавром v2 (docs/product/daily-boss-v2.md § 10, сервер готов 2026-11-03). Ответы —
 * snake_case. Прежние маршруты (`/stories/daily/runs`, `/stories/boss/runs`) работают рядом для старого экрана.
 */

export type BossMood = 'sleepy' | 'lively' | 'fierce';
export type MistakeSource = 'talk' | 'chat' | 'letter' | 'mission' | 'story' | 'boss' | 'typical';

export interface MissionNpc {
  id: string;
  name: string;
  name_ru: string;
  role_ru: string;
  gender: 'f' | 'm';
}

export interface MissionHelp {
  why_ru: string;
  say_en?: string;
  say_ru?: string;
}

/** Урок к поправке: шпаргалка — до трёх строк «Запомни». В миссии — `title_ru`, в бою — `title`. */
export interface V2Lesson {
  slug: string;
  title_ru?: string;
  title?: string;
  cheat?: string[];
}

export interface V2Fix {
  wrong: string;
  right: string;
  why_ru: string;
  topic?: string | null;
  lesson?: V2Lesson | null;
}

export interface V2Pron {
  heard: string;
  meant: string;
  tip_ru: string;
  audio?: string | null;
}

export interface MissionLine {
  who: 'npc' | 'me';
  en: string;
  ru?: string | null;
  emo?: number | null;
  audio?: string | null;
  reaction_ru?: string | null;
  fixes?: V2Fix[];
  pron?: V2Pron[];
  not_english?: boolean;
}

export interface MissionHint {
  tactic_ru: string;
  en: string;
  ru: string;
}

export interface MissionResult {
  done: boolean;
  perfect: boolean;
  stars: number;
  items_done: number;
  items_total: number;
  bonus_done: string[];
  fixes: V2Fix[];
  pron: V2Pron[];
  words: [string, string][];
  /** Начислено за это прохождение (жетоны) или null. */
  reward: number | null;
  replay: boolean;
  next_at: string;
}

export interface Mission {
  run_id: string;
  mission_id: string;
  status: 'active' | 'finished' | 'abandoned' | 'failed';
  ended: boolean;
  title_ru: string;
  level: LearnerLevel;
  learner_level: LearnerLevel | null;
  need_level: boolean;
  place: string;
  place_ru: string;
  npc: MissionNpc;
  task: { title_ru: string; items: Record<string, string>; help: Record<string, MissionHelp> };
  bonus: Record<string, string>;
  lines: MissionLine[];
  hints: MissionHint[];
  /** У `zero` подсказки открыты сразу. */
  hints_open: boolean;
  think_ru: string;
  done: string[];
  bonus_done: string[];
  words: [string, string][];
  /** Сколько ответов записано — его слать в `turn` следующего ответа (повтор после обрыва — 409 STORY_TURN_STALE). */
  turn: number;
  /** Сколько засчитано в длину миссии (ответ не по-английски записан, но не засчитан). */
  replies: number;
  max_turns: number;
  free: boolean;
  replay: boolean;
  reward: { done: number; perfect: number; perfect_max_fixes: number };
  granted: number | null;
  fixes_total: number;
  result: MissionResult | null;
  /** Повтор тратит силы игры (первое прохождение дня — нет). */
  costs_energy?: boolean;
  energy?: { left: number; max: number; bonus: number } | null;
}

export interface MissionTurn {
  new_titles?: TitleGain[];
  me: { who: 'me'; en: string; fixes: V2Fix[]; pron: V2Pron[] };
  line: MissionLine;
  reaction_ru: string | null;
  fixes: V2Fix[];
  pron: V2Pron[];
  hints: MissionHint[];
  think_ru: string;
  done: string[];
  bonus_done: string[];
  ended: boolean;
  /** Модель не ответила — житель просит повторить, ничего не засчитано. */
  fallback: boolean;
  not_english: boolean;
  turn: number;
  replies: number;
  max_turns: number;
  fixes_total: number;
  result: MissionResult | null;
  bonus_granted: StoryBonusGrant[];
  transcript?: string;
  /** Все пункты закрыты, разговор продолжается (2026-11-03). */
  task_done?: boolean;
  /** Повтор тратит силы игры: остаток после хода. */
  costs_energy?: boolean;
  energy?: { left: number; max: number; bonus: number } | null;
}

/** Ответ модели бывает долгим (реплика + правщик + озвучка): ждём, как в разговорах новой игры. */
const TALK_TIMEOUT = 60_000;

function audioForm(audio: Blob, fields: Record<string, string | number | undefined>): FormData {
  const form = new FormData();
  const ext = audio.type.includes('mp4') ? 'm4a' : audio.type.includes('ogg') ? 'ogg' : 'webm';
  form.append('audio', audio, `reply.${ext}`);
  for (const [k, v] of Object.entries(fields)) if (v !== undefined) form.append(k, String(v));
  return form;
}

export async function startMission(restart = false): Promise<Mission> {
  const { data } = await api.post<Mission>('/stories/mission/start', restart ? { restart } : {}, { timeout: TALK_TIMEOUT });
  return data;
}

export async function fetchMission(runId: string): Promise<Mission> {
  const { data } = await api.get<Mission>(`/stories/mission/${runId}`);
  return data;
}

export async function missionReply(runId: string, text: string, turn: number): Promise<MissionTurn> {
  const { data } = await api.post<MissionTurn>(`/stories/mission/${runId}/reply`, { text, turn }, { timeout: TALK_TIMEOUT });
  return data;
}

export async function missionVoice(runId: string, audio: Blob, turn: number): Promise<MissionTurn> {
  const { data } = await api.post<MissionTurn>(`/stories/mission/${runId}/voice`, audioForm(audio, { turn }), {
    timeout: TALK_TIMEOUT + 20_000,
  });
  return data;
}

// ---------------------------------------------------------------------------------------------- бой v2

/** Поза Граммозавра для рисунка. */
export type BossPose = 'calm' | 'angry' | 'laugh' | 'hit' | 'panic' | 'dance' | 'defeated';
export type RoundType = 'choose' | 'find' | 'gap' | 'order' | 'fix' | 'say' | 'mirror' | 'listen' | 'trap' | 'duel';
export type RoundKind = 'mine' | 'typical' | 'pron' | 'trap' | 'duel';
export type JarState = 'full' | 'burst' | 'back' | 'kept';
export type BossReaction = 'hit' | 'crit' | 'miss' | 'panic' | 'mercy' | 'skip';

export interface BossLine {
  en: string;
  ru: string;
  mood: BossPose;
  audio?: string | null;
  /** Шутка с фактом об ученике (из памяти жителей). */
  joke?: boolean;
}

export interface BossJar {
  id: string;
  phrase: string;
  kind: RoundKind;
  mine: boolean;
  state: JarState;
}

export interface BossBox {
  name: string;
  hp: number;
  max_hp: number;
  lives: number;
  max_lives: number;
  round: number;
  rounds: number;
  level: number;
  mood: BossMood;
}

export interface BossRound {
  no: number;
  type: RoundType;
  kind: RoundKind;
  task_ru: string;
  jar_id: string | null;
  mine: boolean;
  source: MistakeSource | null;
  source_ru: string | null;
  /** Кому ученик это сказал (имя жителя). */
  said_to: string | null;
  /** Промах вернулся последним раундом. */
  second_chance: boolean;
  /** Промахивался в прошлых боях. */
  repeat: boolean;
  /** Правило (по нажатию). */
  hint: string | null;
  topic: string | null;
  /** Таймер: только «Свирепый»; у дуэли — 15 с всегда. */
  time_limit_s: number | null;
  line: BossLine | null;
  /** Подсказка «по доброте» после трёх промахов подряд. */
  hint_ru: string | null;
  options?: string[];
  tokens?: string[];
  sentence?: string;
  text?: string;
  end?: string;
  audio?: string | null;
  say_text?: string;
  items?: string[];
  word?: string;
  heard?: string;
  tip_ru?: string;
  phrase?: string | null;
}

export interface BossFeedback {
  type: RoundType;
  kind: RoundKind;
  given: string;
  by: 'rule' | 'ai' | 'fallback' | 'timeout' | 'skip';
  right: string | null;
  why: string | null;
  rule: string | null;
  topic: string | null;
  lesson: V2Lesson | null;
  right_option?: number;
  wrong_tokens?: number[];
  no_mistake?: boolean;
  score?: number;
  combo?: number;
  truth?: boolean[];
  total?: number;
  word?: string;
  heard?: string;
  tip_ru?: string | null;
  fixes?: V2Fix[];
  corrected?: string;
  praise_ru?: string;
  pron?: V2Pron[];
  quick?: boolean;
}

export interface BossClosed {
  jar_id: string;
  kind: RoundKind;
  sentence: string;
  wrong: string | null;
  right: string | null;
  why: string | null;
  topic: string | null;
  lesson: V2Lesson | null;
  source: MistakeSource | null;
}

export interface BossLeft {
  kind: 'grammar' | 'pron' | string;
  wrong: string;
  right: string;
  why: string;
  topic: string | null;
  lesson: V2Lesson | null;
  source: MistakeSource | null;
  source_ru: string | null;
  said_to: string | null;
  repeats: number;
}

export interface BossResultV2 {
  win: boolean;
  stars: number;
  rounds: {
    no: number;
    type: RoundType;
    kind: RoundKind;
    jar_id: string | null;
    sentence: string;
    right: string;
    answer: string;
    ok: boolean | null;
    mine: boolean;
    source: MistakeSource | null;
    damage: number;
    reaction: BossReaction | null;
    rule: string;
    topics: string[];
  }[];
  resolved: number;
  closed: BossClosed[];
  left: BossLeft[];
  open_left: number;
  damage: number;
  level: number;
  level_up: boolean;
  next_level: number;
  next_hat: string | null;
  next_hat_ru: string | null;
  /** Звания, полученные этим боем. */
  new_titles?: TitleGain[];
}

export interface BossFight {
  run_id: string;
  status: 'active' | 'finished' | 'abandoned' | 'failed';
  v: 2;
  mood: BossMood;
  mood_ru: string;
  moods: { id: BossMood; title_ru: string }[];
  boss_level: number;
  hat: string | null;
  hat_ru: string | null;
  outfit: string | null;
  /** Наряд на голову рисуется вместо шляпы; `birthday` — праздничная шляпа и торт. */
  outfit_slot: '' | 'head' | 'face' | 'body' | 'neck' | null;
  boss: BossBox;
  intro: BossLine | null;
  jars: BossJar[];
  round: BossRound | null;
  streak: number;
  /** Следующий верный — крит. */
  crit_ready: boolean;
  price_tokens: number;
  learner_level: LearnerLevel | null;
  need_level: boolean;
  result: BossResultV2 | null;
}

export interface BossAnswer {
  ok: boolean | null;
  reaction: BossReaction;
  line: BossLine | null;
  final_line: BossLine | null;
  damage: number;
  quick: boolean;
  hint_ru: string | null;
  jar_id: string | null;
  jar_state: Exclude<JarState, 'full'> | null;
  feedback: BossFeedback;
  boss: BossBox;
  streak: number;
  next: BossRound | null;
  finished: boolean;
  result: BossResultV2 | null;
  transcript?: string;
}

/** Ответ на раунд — по виду раунда (§ 10.3). */
export type BossRoundBody =
  | { choice: number }
  | { order: number[] }
  | { text: string }
  | { no_mistake: true }
  | { marks: (boolean | null)[] }
  | { timeout: true }
  | { skip: true };

/** Тело запроса: номер раунда (защита от повтора), сколько думал («Свирепый») и сам ответ. */
export type BossAnswerBody = { round: number; answered_in_ms?: number } & BossRoundBody;

export async function startBossV2(body: { mood?: BossMood; restart?: boolean; mic?: boolean }): Promise<BossFight> {
  const { data } = await api.post<BossFight>('/stories/boss/start', body, { timeout: TALK_TIMEOUT });
  return data;
}

export async function fetchBossV2(runId: string): Promise<BossFight> {
  const { data } = await api.get<BossFight>(`/stories/boss/${runId}`);
  return data;
}

export async function answerBoss(runId: string, body: BossAnswerBody): Promise<BossAnswer> {
  const { data } = await api.post<BossAnswer>(`/stories/boss/${runId}/answer`, body, { timeout: TALK_TIMEOUT });
  return data;
}

export async function answerBossVoice(runId: string, audio: Blob, round: number, answeredInMs?: number): Promise<BossAnswer> {
  const { data } = await api.post<BossAnswer>(
    `/stories/boss/${runId}/voice`,
    audioForm(audio, { round, answered_in_ms: answeredInMs }),
    { timeout: TALK_TIMEOUT + 20_000 },
  );
  return data;
}

// ---------------------------------------------------------------------------------------------- хаб v2

/** Поля миссии дня v2 в `GET /stories` → `daily` (рядом с прежними). */
export interface DailyV2Fields {
  v?: 2;
  place?: string;
  place_ru?: string;
  npc?: MissionNpc;
  level?: LearnerLevel;
  items?: Record<string, string>;
  bonus?: Record<string, string>;
  help?: Record<string, MissionHelp>;
  words?: [string, string][];
  /** Сегодня начата миссия v2. */
  active_v2?: boolean;
  /** Сегодня уже пройдена — новое прохождение без награды. */
  replay?: boolean;
  perfect_max_fixes?: number;
}

/** Звание Граммозавра (`stories/titles.py`, владелец 2026-11-04). */
export interface TitleGain {
  id: string;
  name_ru: string;
  /** Как получить: «Отбей 5 ошибок с a, an и the». */
  how_ru: string;
  line_en: string;
  line_ru: string;
}

export interface TitleItem extends TitleGain {
  need: number;
  progress: number;
  earned_at: string | null;
}

export interface TitlesInfo {
  earned: number;
  total: number;
  /** Полученные сверху, свежие первыми; дальше — в порядке каталога. */
  items: TitleItem[];
}

/** Граммозавр v2 в `GET /stories` → `boss.v2`. */
export interface BossV2Info {
  open: number;
  closed: number;
  pron: number;
  sources: Partial<Record<Exclude<MistakeSource, 'typical'>, number>>;
  level: number;
  hat: string | null;
  hat_ru: string | null;
  outfit: string | null;
  outfit_slot: string | null;
  mood: BossMood;
  moods: { id: BossMood; title_ru: string }[];
  rounds: { min: number; max: number };
  lives: Record<BossMood, number>;
  types: RoundType[];
  price_tokens: number;
  titles?: TitlesInfo | null;
}

/** «Выйти» с итогом: закончить миссию сейчас (2026-11-03). Повтор безопасен. */
export async function missionBye(runId: string): Promise<Mission & { close?: MissionLine | null }> {
  const { data } = await api.post<Mission & { close?: MissionLine | null }>(`/stories/mission/${runId}/bye`, undefined, {
    timeout: TALK_TIMEOUT,
  });
  return data;
}
